import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { isEmpty as _isEmpty, cloneDeep as _cloneDeep } from 'lodash';
import PropTypes from 'prop-types';

import { makeMapStateToProps } from 'reducers/oddsProjection';
import BSOdds from 'newDesign/modules/live/components/bettingSuggestions/BSOdds';
import { useIntl } from 'react-intl';
import useBSOdds from 'hooks/BSOddsHook';

// Helper to mark a market as betstopped/disabled odds
const createBetStoppedMarket = market => {
  if (!market) {
    return null;
  }

  const clonedMarket = _cloneDeep(market);
  clonedMarket.betstop = true;

  if (clonedMarket.outcomes && clonedMarket.outcomes.length > 0) {
    clonedMarket.outcomes[0].betstop = true;
  }

  return clonedMarket;
};

const Event = ({
  marketsOutcomesArr,
  marketsNameShortArr,
  processingEvent,
  setHasOdds,
  setUsedMarket,
  setUsedMarket2,
  tempUsedMarket,
  tempUsedMarket2,
}) => {
  const intl = useIntl();

  const memoizedMarketsOutcomesArr = useMemo(
    () => marketsOutcomesArr,
    [marketsOutcomesArr],
  );
  const memoizedMarketsNameShortArr = useMemo(
    () => marketsNameShortArr,
    [marketsNameShortArr],
  );

  const eventString = processingEvent && processingEvent.sentence ? processingEvent.sentence : '';
  const eventCallToAction = intl.formatMessage({ id: 't_bs_call_to_action' });

  const {
    usedMarket,
    usedMarket2,
  } = useBSOdds(memoizedMarketsOutcomesArr, memoizedMarketsNameShortArr);

  useEffect(() => {
    // Local copies to resolve fallbacks without mutating hook results
    let finalUsedMarket = usedMarket;
    let finalUsedMarket2 = usedMarket2;

    // Deterministic fallback: if empty, use temp*
    if (_isEmpty(finalUsedMarket) && !_isEmpty(tempUsedMarket)) {
      finalUsedMarket = tempUsedMarket;
    }

    if (_isEmpty(finalUsedMarket2) && !_isEmpty(tempUsedMarket2)) {
      finalUsedMarket2 = tempUsedMarket2;
    }

    // Mirror into redux state with betstop flags
    if (!_isEmpty(finalUsedMarket) && !_isEmpty(tempUsedMarket)) {
      const processedTempUsedMarket = createBetStoppedMarket(finalUsedMarket);
      setUsedMarket(processedTempUsedMarket);
    }

    if (!_isEmpty(finalUsedMarket2) && !_isEmpty(tempUsedMarket2)) {
      const processedTempUsedMarket2 = createBetStoppedMarket(finalUsedMarket2);
      setUsedMarket2(processedTempUsedMarket2);
    }

    // Set has odds based on final values
    setHasOdds(!_isEmpty(finalUsedMarket));
  }, [
    usedMarket,
    usedMarket2,
    tempUsedMarket,
    tempUsedMarket2,
    setHasOdds,
    setUsedMarket,
    setUsedMarket2,
  ]);

  return (
    <div className='w-[64vw] max-w-[64vw] overflow-hidden bg-[rgba(0,4,7,0.5)] backdrop-blur-[20px]'>
      {/* Content */}
      <div className='py-[2vw]'>
        {/* Header */}
        <div className='flex items-center justify-center gap-[1vw]'>
          <h2 className='uppercase fs-25 font-second--bold tracking-wide text-white'>
            {eventString}
          </h2>

          {processingEvent
            && processingEvent.trigger === 'red_card' && (
              <span className='h-[2.3vw] w-[1.7vw] rounded-[0.3vw] bg-red-600' />
          )}
        </div>

        <div className='uppercase text-center fs-24 font-second--bold tracking-wide text-lime-400 color-theme-highlight'>
          {eventCallToAction}
        </div>

        <BSOdds
          usedMarket={usedMarket}
          usedMarket2={usedMarket2}
        />
      </div>
    </div>
  );
};

Event.propTypes = {
  marketsOutcomesArr: PropTypes.array,
  marketsNameShortArr: PropTypes.array,
  processingEvent: PropTypes.object,
  setHasOdds: PropTypes.func,
  setUsedMarket: PropTypes.func,
  setUsedMarket2: PropTypes.func,
  tempUsedMarket: PropTypes.object,
  tempUsedMarket2: PropTypes.object,
};

Event.defaultProps = {
  marketsOutcomesArr: [],
  marketsNameShortArr: [],
  processingEvent: null,
  setHasOdds: () => null,
  setUsedMarket: () => null,
  setUsedMarket2: () => null,
  tempUsedMarket: null,
  tempUsedMarket2: null,
};

export default connect(makeMapStateToProps)(Event);
